/**
 * News Ticker Component
 * Horizontal scrolling news ticker using NewsAPI
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiExternalLink } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const NewsTicker = ({ country = 'us', category }) => {
  const { isAdmin } = useAuth();
  const [headlines, setHeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tickerRef = useRef(null);

  useEffect(() => {
    const fetchHeadlines = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = { country, pageSize: 10 };
        if (category) params.category = category;
        
        const response = await api.get('/news/headlines', { params });
        if (response.data.success) {
          setHeadlines(response.data.data || []);
        } else {
          setError(response.data.message || 'Failed to fetch news');
        }
      } catch (err) {
        console.error('News fetch error:', err);
        setError(err.response?.data?.message || err.message || 'Unable to load news');
      } finally {
        setLoading(false);
      }
    };

    fetchHeadlines();
    // Refresh every 30 minutes
    const interval = setInterval(fetchHeadlines, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [country, category]);

  // Format relative time
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <NewsTickerContainer>
        <TickerContent>
          <LoadingText>Loading news...</LoadingText>
        </TickerContent>
      </NewsTickerContainer>
    );
  }

  if (error) {
    if (isAdmin) {
      return (
        <NewsTickerContainer>
          <TickerContent>
            <ErrorText>⚠️ News unavailable: {error}</ErrorText>
          </TickerContent>
        </NewsTickerContainer>
      );
    }
    return null; // Hide silently for non-admin users
  }

  if (!headlines || headlines.length === 0) {
    return null;
  }

  // Duplicate headlines for seamless loop
  const duplicatedHeadlines = [...headlines, ...headlines];

  return (
    <NewsTickerContainer>
      <TickerLabel>Latest News</TickerLabel>
      <TickerWrapper ref={tickerRef}>
        <TickerContent $itemCount={duplicatedHeadlines.length}>
          {duplicatedHeadlines.map((headline, index) => (
            <NewsItem
              key={`${headline.url}-${index}`}
              href={headline.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!headline.url || headline.url === '#') {
                  e.preventDefault();
                }
              }}
            >
              <NewsTitle>{headline.title}</NewsTitle>
              <NewsMeta>
                <NewsSource>{headline.source}</NewsSource>
                <NewsTime>{getRelativeTime(headline.publishedAt)}</NewsTime>
              </NewsMeta>
              <NewsIcon>
                <FiExternalLink size={14} />
              </NewsIcon>
            </NewsItem>
          ))}
        </TickerContent>
      </TickerWrapper>
    </NewsTickerContainer>
  );
};

const NewsTickerContainer = styled.div`
  width: 100%;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  padding: 1rem 0;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const TickerLabel = styled.div`
  flex-shrink: 0;
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--accent-primary);
  padding: 0 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    font-size: 0.75rem;
  }
`;

const TickerWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 2%,
    black 98%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 2%,
    black 98%,
    transparent 100%
  );
`;

const TickerContent = styled.div`
  display: inline-flex;
  gap: 3rem;
  animation: scroll ${props => (props.$itemCount || 10) * 3}s linear infinite;
  white-space: nowrap;
  
  &:hover {
    animation-play-state: paused;
  }

  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-50%);
    }
  }
`;

const NewsItem = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  text-decoration: none;
  color: var(--text-primary);
  transition: all 0.2s ease;
  flex-shrink: 0;
  border: 1px solid transparent;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: var(--accent-primary, rgba(59, 130, 246, 0.3));
    transform: translateY(-2px);
  }
`;

const NewsTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    max-width: 250px;
    font-size: 0.85rem;
  }
`;

const NewsMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  flex-shrink: 0;
`;

const NewsSource = styled.span`
  font-weight: 600;
  opacity: 0.8;
`;

const NewsTime = styled.span`
  opacity: 0.7;
`;

const NewsIcon = styled.span`
  display: flex;
  align-items: center;
  opacity: 0.6;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
  
  ${NewsItem}:hover & {
    opacity: 1;
  }
`;

const LoadingText = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  padding: 0.5rem 1.5rem;
`;

const ErrorText = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  padding: 0.5rem 1.5rem;
`;

export default NewsTicker;

